/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { CardGridGroup } from './CardGrid';
import {
  customMetric,
  getGroup,
  getSubGroup,
  GroupTypes,
  Group,
} from '@g17eco/core';
import { CardProps } from '../../types/surveyScope';
import { ViewValues } from '../survey-overview-sidebar/viewOptions';
import { filterStandardAndFramework } from '../survey/utils/filters';
import { getScopeInfo } from './scopeSelection';
import { convertToFilters } from '../survey/utils/useScopeFilters';
import { findChildGroupByCode } from '../../utils/groups';
import { QUESTION } from '../../constants/terminology';

interface Filters {
  type: string,
  value?: string
}

const findCurrentGroup = (filters: Filters[], group?: Group): Group | undefined => {

  if (!group) {
    return;
  }

  const filterGroups = filters.filter(f => f.type === ViewValues.Group);
  let currentGroup: Group | undefined = group;
  for (const filter of filterGroups) {
    if (!filter.value) {
      return;
    }
    currentGroup = findChildGroupByCode(group, filter.value);
  }

  return currentGroup;
}

export const getGroupCards = ({ questionList, addBtn, handleDrilldown, breadcrumbs, view, surveyData, materiality }: CardProps) => {
  const cardGroups: CardGridGroup[] = [];

  const [viewFilter, ...groupFilters] = convertToFilters(view, breadcrumbs);

  if (!viewFilter.value) {
    return cardGroups;
  }
  const filter =
    viewFilter.type === ViewValues.QuestionPacks || viewFilter.type === ViewValues.AllPacks
      ? ViewValues.StandardsAndFrameworks
      : viewFilter.type;

  const group = getGroup(filter as GroupTypes, viewFilter.value);

  if (!group?.subgroups) {
    return cardGroups;
  }

  const currentSubGroup = findCurrentGroup(groupFilters, group)

  if (!currentSubGroup || !currentSubGroup.subgroups) {
    return cardGroups;
  }

  const cards: CardGridGroup = {
    cards: []
  };

  const getButtons = (code: string, name: string) => {

    const subGroup = getSubGroup(currentSubGroup, code);
    const hasSubGroups = subGroup && subGroup.subgroups && subGroup.subgroups.length > 0;
    const buttons = [];

    if (hasSubGroups) {
      buttons.push({
        icon: <i className='fa fa-list text-ThemeTextPlaceholder' />,
        tooltip: `${name} Groups - Click to view`,
        onClick: () => handleDrilldown(ViewValues.Group, code, name)
      });
    } else {
      buttons.push({
        icon: <i className='fa fa-list text-ThemeTextPlaceholder' />,
        tooltip: `${name} ${QUESTION.CAPITALIZED_PLURAL} - Click to view`,
        onClick: () => handleDrilldown(ViewValues.QuestionList, code, name)
      });
    }
    buttons.push(addBtn(code, name, group));
    return buttons;
  }

  currentSubGroup.subgroups.forEach((subGroup) => {
    const { code, name, src, description } = subGroup;
    const filteredQuestionList = questionList.filter(q => filterStandardAndFramework(q, code))
    if (filteredQuestionList.length === 0) {
      return;
    }
    const logoSrc = src ?? group.src ?? customMetric.src;
    const scopeTag = code;
    const { inScope, isPartial } = getScopeInfo(surveyData.scope, scopeTag, ViewValues.StandardsAndFrameworks, breadcrumbs, materiality);
    cards.cards.push({
      key: `scope-cardgriditem-group-${code}`,
      title: <span>{name}</span>,
      sortTitle: name,
      description,
      icon: <img src={logoSrc} alt={name} width='40px' />,
      unitCount: filteredQuestionList.length,
      buttons: getButtons(code, name),
      scopeTag,
      inScope,
      isPartial
    });
  });

  cardGroups.push(cards);
  return cardGroups;
}
